<template>
  <div class="media-card text-[#000]">
    <!-- <pre class="overflow-x-auto overflow-y-auto">{{ props}}</pre> -->
  <MediaContainerLayer
    v-if="mediaCardState"
    :media-card-state="mediaCardState"
    :max-height="maxHeight"
    :min-height="minHeight"
    :object-fit="objectFit"
    :media-type="mediaType"
    :image-props="imageProps"
    :video-props="videoProps"
    :mask-props="maskProps"
    :has-mask="maskEnabled"
    :mode="mode"
    v-bind="$attrs"
  >
    <template #default="{ isVisible }">
      <ShadowLayer :shadow-props="shadowProps">
        <template #default="{ shadowStyle }">
          <MaskLayer
            :media-card-state="mediaCardState"
            :mask-props="maskProps"
            :object-fit="objectFit"
            class="flex align-center justify-center"
            :style="{
              borderRadius: mediaCardState.borderRadius,
            }"
          >
            <MediaTransformLayer
              :media-card-state="mediaCardState"
              :media-type="mediaType"
              :image-props="imageProps"
              :video-props="videoProps"
              :object-fit="objectFit"
              :shadow-style="shadowStyle"
            >
              <MediaContentLayer
                :media-card-state="mediaCardState"
                :media-type="mediaType"
                :image-props="imageProps"
                :video-props="videoProps"
                :is-visible="isVisible"
                :shadow-props="shadowProps"
                :mask-props="maskProps"
                :object-fit="objectFit"
              />
            </MediaTransformLayer>
          </MaskLayer>
        </template>
      </ShadowLayer>
    </template>
  </MediaContainerLayer>
  </div>
</template>

<script setup lang="ts">
import { computed, watchEffect } from "vue";
import { useMediaCard } from "./MediaCard/composables/useMediaCard";

// Отключаем автоматическое наследование атрибутов, так как мы передаем их явно
defineOptions({
  inheritAttrs: false,
});
import MediaContainerLayer from "./MediaCard/layers/MediaContainerLayer.vue";
import ShadowLayer from "./MediaCard/layers/ShadowLayer.vue";
import MaskLayer from "./MediaCard/layers/MaskLayer.vue";
import MediaTransformLayer from "./MediaCard/layers/MediaTransformLayer.vue";
import MediaContentLayer from "./MediaCard/layers/MediaContentLayer.vue";

export interface VideoProps {
  videoSrc?: string;
  autoplay?: boolean;
  muted?: boolean;
  loop?: boolean;
  zoom?: number;
  positionX?: number;
  positionY?: number;
  width?: number;
  height?: number;
  containerWidth?: number | null;
  containerHeight?: number | null;
  containerPreset?: {
    width?: number | null;
    height?: number | null;
    aspectRatio?: number | null;
  };
}

export interface ShadowProps {
  enabled?: boolean;
  blur?: number;
  offsetX?: number;
  offsetY?: number;
  color?: string;
  opacity?: number;
}

export interface ImageProps {
  imageSrc?: string;
  zoom?: number;
  positionX?: number;
  positionY?: number;
  width?: number;
  height?: number;
  containerWidth?: number | null;
  containerHeight?: number | null;
  containerPreset?: {
    width?: number | null;
    height?: number | null;
    aspectRatio?: number | null;
  };
}

export interface MaskProps {
  shape?: string;
  enabled?: boolean;
  positionX?: number;
  positionY?: number;
  backgroundColor?: string;
  showBackground?: boolean;
  shadow?: {
    enabled?: boolean;
    blur?: number;
    offsetX?: number;
    offsetY?: number;
    color?: string;
    opacity?: number;
  };
  align?: string;
  maskAspectRatio?: number;
}

const props = defineProps<{
  mediaType?: "image" | "video";

  // Группированные props
  videoProps?: VideoProps;
  imageProps?: ImageProps;
  shadowProps?: ShadowProps;

  // Стили
  maskProps?: MaskProps;
  borderRadius?: number;
  width?: string;
  height?: string;
  maxHeight?: string;
  minHeight?: string;
  aspectRatio?: number;
  objectFit?: "cover" | "contain";
  mode?: "view" | "edit";
}>();
  
// Определяем режимы отображения для совместимости
const maskEnabled = computed(() => {
  const maskShape = props.maskProps?.shape || "square";
  const hasMask = !!maskShape && maskShape !== "square";
  return hasMask && (props.maskProps?.enabled ?? false);
});

// Используем централизованный composable для всех вычислений
// Використовуємо computed, щоб mediaCardState оновлювався при зміні props
const mediaCardState = computed(() => useMediaCard({
  mediaType: props.mediaType,
  width: props.width,
  height: props.height,
  maxHeight: props.maxHeight,
  minHeight: props.minHeight,
  aspectRatio: props.aspectRatio,
  borderRadius: props.borderRadius,
  objectFit: props.objectFit,
  imageProps: props.imageProps,
  videoProps: props.videoProps,
  maskProps: props.maskProps,
  hasMask: maskEnabled.value,
}));

watchEffect(() => {
  // console.log(props);
});


</script>

<style scoped>
.media-card {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
}

</style>
